import React, { useState, useRef } from 'react';
import { Modal, Select } from 'antd';
import * as Yup from 'yup';
import { Field } from 'formik';
import { FormatText, FormSubmitButton, MasterDetailForm } from '@digital/ui';
import { alterarFornecedorContrato } from '@app/api/contrato/geral';
import { getFornecedoresByTerm } from '@app/api/fornecedor';
import { vtDebounce } from '@app/hooks/useDebouncedValue';

const AntModal = Modal as any;
const AntSelect = Select as any;
const AntField = Field as any;
const { Option } = Select;

interface ModalAlterarFornecedorProps {
  contratoId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FornecedorSelectProps {
  onSelect: (value: string) => void;
  error?: string;
}

const FornecedorSelectInput = ({ onSelect, error }: FornecedorSelectProps) => {
  const [options, setOptions] = useState<{ text: string | undefined; value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useRef(
    vtDebounce((term: string) => {
      if (!term || term.length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      getFornecedoresByTerm(term)
        .then(setOptions)
        .finally(() => setLoading(false));
    }, 450),
  ).current;

  return (
    <div style={{ marginBottom: 16 }}>
      <label>
        <span style={{ color: 'red' }}>* </span>Fornecedor
      </label>
      <AntSelect
        showSearch
        filterOption={false}
        onSearch={debouncedSearch}
        onChange={onSelect}
        loading={loading}
        notFoundContent={loading ? 'Buscando...' : 'Nenhum fornecedor encontrado'}
        placeholder="Digite para buscar fornecedor"
        style={{ width: '100%' }}
      >
        {options.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.text}
          </Option>
        ))}
      </AntSelect>
      {error && (
        <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
};

const ModalAlterarFornecedor = ({ contratoId, visible, onClose, onSuccess }: ModalAlterarFornecedorProps) => {
  const [carregando, setCarregando] = useState(false);

  const handleSubmit = (values: any) => {
    if (!values.fornecedorId) return;
    setCarregando(true);
    alterarFornecedorContrato(contratoId, values.fornecedorId)
      .then(() => {
        onSuccess();
        onClose();
      })
      .finally(() => setCarregando(false));
  };

  return (
    <AntModal
      title="Alterar Fornecedor do Contrato"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="520px"
    >
      {visible && (
        <MasterDetailForm
          onSubmit={handleSubmit}
          initialValues={{ fornecedorId: undefined }}
          validationSchema={Yup.object().shape({
            fornecedorId: Yup.string().required('Selecione um fornecedor'),
          })}
        >
          <p>Selecione o novo fornecedor. O fornecedor do contrato e os usuários vinculados serão atualizados.</p>
          <AntField name="fornecedorId">
            {({ form }: any) => (
              <FornecedorSelectInput
                onSelect={(value) => {
                  form.setFieldValue('fornecedorId', value);
                  form.setFieldTouched('fornecedorId', true, false);
                }}
                error={form.touched.fornecedorId && form.errors.fornecedorId}
              />
            )}
          </AntField>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <FormSubmitButton noIcon type="primary" loading={carregando}>
              <FormatText id="ui.commons.save" uppercase />
            </FormSubmitButton>
          </div>
        </MasterDetailForm>
      )}
    </AntModal>
  );
};

export default ModalAlterarFornecedor;
